// ================================================================
// E-Mail-View — Posteingang (Liste + Split-Ansicht), Gmail-Sync/Connect,
// KI-Analyse (einzeln/alle), Antwort-Vorschläge, E-Mail-Modal.
// ================================================================
// Herausgelöst aus CompanyDashboard.js (Datei-Split, Runde 3 — 2026-07-19).
// Gleicher Injektions-Ansatz wie die übrigen View-Module.
import { t } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';
import { dateTime as fmtDateTime } from '../../utils/format.ts';
import { listConnections, pickMailConnection, syncGmail, listStoredEmails, analyzeEmail, deleteEmail, getEmail, suggestEmail, setEmailRead } from '../../core/email.js';
import { startOAuthFlow } from '../../core/oauth.js';
import { emailKpis, filterBar, emailCatLabel } from './dashboardWidgets.js';

let s;
let draw;
let can;
let grad;
let initials;

/** Einmal beim Modul-Start von CompanyDashboard aufrufen. */
export function initEmailView(ctx) {
  ({ s, draw, can, grad, initials } = ctx);
}

export function filterEmails(arr, cat) {
  if (!cat || cat === 'all') return arr;
  return arr.filter((m) => m.aiCategory === cat);
}

export function syncSplitSelection() {
  if (s.emailLayout !== 'split') return;
  const stillThere = s.selectedEmailId && s.emails.some((e) => e.id === s.selectedEmailId);
  if (stillThere) return;
  if (s.emails.length) {
    selectEmail(s.emails[0].id);
  } else {
    s.selectedEmailId = null;
    s.selectedEmail = null;
    s.selectedLoading = false;
  }
}

export function toggleFilter(cat) {
  s.emailCategory = (s.emailCategory === cat) ? 'all' : cat;
  s.emails = filterEmails(s.allEmails, s.emailCategory);
  syncSplitSelection();
  draw();
}

export async function loadEmails() {
  s.emailLoading = true; s.emailError = ''; s.emailSyncWarning = ''; s.emailLoaded = true; draw();
  try {
    const { connections } = await listConnections();
    // Postfach, nicht irgendeine Verbindung: unter /api/connections liegen auch
    // die Kalender-Freigaben (core/email.js, pickMailConnection).
    const g = pickMailConnection(connections);
    if (g) {
      s.emailAccount = g.email || '';
      s.emailConnectionId = g.id; // baraye ersal az tab-e Emails
      // Der Sync darf die Ansicht nie blockieren — die Liste kommt gleich aus
      // der Datenbank. Ihn aber ganz zu verschlucken war das andere Extrem:
      // ist die Gmail-Freigabe abgelaufen (bekannter Wiederholungsfall), sah
      // der Nutzer einfach für immer einen veralteten Posteingang und erfuhr
      // nie, dass er das Postfach neu verbinden muss. Darum als Hinweiszeile
      // ÜBER der Liste statt als Fehler ANSTELLE der Liste.
      try {
        await syncGmail(g.id);
      } catch (e) {
        s.emailSyncWarning = e.message || t('dashboard.email.errSync');
      }
    }
    const res = await listStoredEmails('all');
    s.allEmails = res.emails || [];
    s.emailUnread = typeof res.unread === 'number' ? res.unread : 0;
    s.emails = filterEmails(s.allEmails, s.emailCategory);
    if (!g && !s.emails.length) {
      s.emailError = t('dashboard.email.errNoAccount');
    }
  } catch (e) {
    s.emailError = e.message || t('dashboard.email.errLoad');
  } finally {
    s.emailLoading = false; draw();
  }
}

export async function connectGmail() {
  if (s.connecting) return;
  s.connecting = true; s.emailError = ''; draw();
  try {
    await startOAuthFlow('google', '/api/auth/google/start');
    s.connecting = false;
    s.emailLoaded = false;
    await loadEmails();
  } catch (e) {
    s.connecting = false;
    s.emailError = e.message || t('dashboard.email.errConnect');
    draw();
  }
}

export function applyAnalysisToState(id, res) {
  for (const arr of [s.emails, s.allEmails]) {
    const m = arr.find((e) => e.id === id);
    if (m) { m.aiCategory = res.category; m.aiSummary = res.summary; m.aiAction = res.action; }
  }
}

export async function removeEmail(id) {
  if (!id) return;
  if (!window.confirm(t('dashboard.email.confirmDelete'))) return;
  try {
    await deleteEmail(id);
    s.allEmails = (s.allEmails || []).filter((e) => e.id !== id);
    s.emails = (s.emails || []).filter((e) => e.id !== id);
    draw();
  } catch (e) {
    s.emailError = e.message || t('dashboard.email.errDelete');
    draw();
  }
}

/**
 * Beim Öffnen als gelesen markieren — lokal SOFORT (der blaue Punkt darf nicht
 * auf das Netz warten), der Server erfährt es nebenher. Schlägt das fehl,
 * korrigiert der nächste Ladevorgang die Anzeige; ein Fehler-Popup wäre hier
 * schlimmer als ein Punkt, der eine Minute zu früh verschwindet.
 */
function markRead(id) {
  const row = (s.allEmails || []).find((e) => e.id === id);
  if (!row || row.readAt) return; // schon gelesen → kein Aufruf
  const now = new Date().toISOString();
  row.readAt = now;
  const shown = (s.emails || []).find((e) => e.id === id);
  if (shown) shown.readAt = now;
  if (typeof s.emailUnread === 'number') s.emailUnread = Math.max(0, s.emailUnread - 1);
  setEmailRead(id, true)
    .then((r) => { if (r && typeof r.unread === 'number') s.emailUnread = r.unread; })
    .catch(() => {});
}

export async function openEmailModal(id) {
  if (!id) return;
  markRead(id);
  // open-email-cache-fix: agar hamun email ghablan load shode, dobare fetch nakon
  if (s.openEmail && s.openEmail.id === id) { draw(); return; }
  s.openEmail = null;
  s.openEmailLoading = true;
  draw();
  try {
    const email = await getEmail(id);
    s.openEmail = email;
  } catch (e) {
    s.emailError = e.message || t('dashboard.email.errLoadOne');
  } finally {
    s.openEmailLoading = false;
    draw();
  }
}

export function closeEmailModal() {
  s.openEmail = null;
  s.openEmailLoading = false;
  draw();
}

export function emailModal() {
  if (!s.openEmail && !s.openEmailLoading) return '';
  let inner;
  if (s.openEmailLoading) {
    inner = `<div class="cd-muted" style="padding:30px;text-align:center;">${escapeHtml(t('common.loading'))}</div>`;
  } else {
    const m = s.openEmail;
    const f = m.fromEmail ? { name: m.fromName || m.fromEmail, email: m.fromEmail } : { name: '', email: '' };
    let when = '';
    if (m.receivedAt) { when = fmtDateTime(m.receivedAt) || String(m.receivedAt); }
    inner = `
      <div class="cd-em-head">
        <div class="cd-em-subject">${escapeHtml(m.subject || t('dashboard.email.noSubject'))}</div>
        <button class="cd-em-close" data-action="close-email" title="${escapeHtml(t('common.close'))}">×</button>
      </div>
      <div class="cd-em-meta">
        <div class="cd-ava" style="background:${grad(f.name || f.email)};">${escapeHtml(initials(f.name, f.email))}</div>
        <div class="cd-em-meta-text">
          <div class="cd-em-from">${escapeHtml(f.name || f.email)}</div>
          <div class="cd-em-email">${escapeHtml(f.email)}</div>
        </div>
        <div class="cd-em-date">${escapeHtml(when)}</div>
      </div>
      <div class="cd-em-body">${escapeHtml(m.body || m.snippet || t('dashboard.email.noContent'))}</div>
    `;
  }
  return `<div class="cd-em-backdrop" data-action="close-email"><div class="cd-em-modal">${inner}</div></div>`;
}

export async function analyzeOne(id) {
  if (!id || (s.analyzingIds && s.analyzingIds.has(id))) return;
  s.analyzingIds.add(id); draw();
  try {
    const res = await analyzeEmail(id);
    applyAnalysisToState(id, res);
  } catch (e) {
    s.emailError = e.message || t('dashboard.email.errAnalyze');
  } finally {
    s.analyzingIds.delete(id); draw();
  }
}

export async function analyzeAll() {
  if (s.analyzingAll) return;
  const todo = s.emails.filter((m) => !m.aiCategory);
  if (!todo.length) return;
  s.analyzingAll = true; draw();
  for (const m of todo) {
    try {
      const res = await analyzeEmail(m.id);
      applyAnalysisToState(m.id, res);
      draw();
    } catch (e) { /* skip */ }
  }
  s.analyzingAll = false; draw();
}

export async function reanalyzeAll() {
  if (s.analyzingAll) return;
  const todo = s.emails.slice(); // همه — حتی تحلیل‌شده‌ها
  if (!todo.length) return;
  s.analyzingAll = true; draw();
  for (const m of todo) {
    try {
      const res = await analyzeEmail(m.id);
      applyAnalysisToState(m.id, res);
      draw();
    } catch (e) { /* skip */ }
  }
  s.analyzingAll = false; draw();
}

export function toggleLayout() {
  s.emailLayout = s.emailLayout === 'split' ? 'list' : 'split';
  if (s.emailLayout === 'split' && !s.selectedEmailId && s.emails.length) {
    selectEmail(s.emails[0].id);
    return;
  }
  draw();
}

export async function selectEmail(id) {
  if (!id) return;
  markRead(id);
  s.selectedEmailId = id;
  s.selectedEmail = null;
  s.selectedLoading = true;
  s.suggestions = [];
  s.suggestForId = null;
  draw();
  try {
    s.selectedEmail = await getEmail(id);
  } catch (e) {
    s.emailError = e.message || t('dashboard.email.errLoadOne');
  } finally {
    s.selectedLoading = false;
    draw();
  }
}

export async function suggestEmailNow(id) {
  if (!id || s.suggestLoading) return;
  s.suggestLoading = true;
  s.suggestForId = id;
  s.suggestions = [];
  draw();
  try {
    const res = await suggestEmail(id);
    s.suggestions = (res && Array.isArray(res.suggestions)) ? res.suggestions : [];
  } catch (e) {
    s.emailError = e.message || t('dashboard.email.errSuggest');
  } finally {
    s.suggestLoading = false;
    draw();
  }
}

export function splitRow(m) {
  const f = m.fromEmail ? { name: m.fromName || m.fromEmail, email: m.fromEmail } : emailFrom(m.from);
  let when = '';
  const rawDate = m.receivedAt || m.date;
  if (rawDate) { when = fmtDateTime(rawDate) || String(rawDate); }
  const active = s.selectedEmailId === m.id ? ' cd-sp-row-active' : '';
  const unread = !m.readAt ? ' cd-mail-unread' : '';
  return `
    <div class="cd-sp-row${active}${unread}" data-action="select-email" data-id="${escapeHtml(m.id)}">
      <span class="cd-mail-dot" aria-hidden="true"></span>
      <div class="cd-ava" style="background:${grad(f.name || f.email)};">${escapeHtml(initials(f.name, f.email))}</div>
      <div class="cd-sp-row-main">
        <div class="cd-sp-row-top">
          <span class="cd-sp-row-from">${escapeHtml(f.name || f.email)}</span>
          <span class="cd-sp-row-date">${escapeHtml(when)}</span>
        </div>
        <div class="cd-sp-row-subject">${escapeHtml(m.subject || t('dashboard.email.noSubject'))}</div>
        <div class="cd-sp-row-snippet">${escapeHtml(m.snippet || '')}</div>
        ${m.aiCategory ? aiCatBadge(m.aiCategory) : ''}
      </div>
    </div>`;
}

export function splitDetail() {
  if (s.selectedLoading) return `<div class="cd-muted" style="padding:40px;text-align:center;">${escapeHtml(t('common.loading'))}</div>`;
  if (!s.selectedEmail) return `<div class="cd-empty" style="padding:40px;text-align:center;">${escapeHtml(t('dashboard.email.split.pickEmail'))}</div>`;
  const m = s.selectedEmail;
  const f = m.fromEmail ? { name: m.fromName || m.fromEmail, email: m.fromEmail } : { name: '', email: '' };
  const dispName = f.name || f.email || '—';
  let when = '';
  if (m.receivedAt) { when = fmtDateTime(m.receivedAt) || String(m.receivedAt); }
  const HIGH = ['urgent_request', 'complaint', 'opportunity'];
  const MED = ['invoice', 'follow_up', 'supplier', 'job_application'];
  let prioLabel = t('dashboard.email.priority.normal'), prioCls = 'cd-dt-low', replyWindow = t('dashboard.email.replyWindow.none');
  if (HIGH.indexOf(m.aiCategory) !== -1) { prioLabel = t('dashboard.email.priority.high'); prioCls = 'cd-dt-high'; replyWindow = t('dashboard.email.replyWindow.h24'); }
  else if (MED.indexOf(m.aiCategory) !== -1) { prioLabel = t('dashboard.email.priority.medium'); prioCls = 'cd-dt-med'; replyWindow = t('dashboard.email.replyWindow.d3'); }
  const catLabel = m.aiCategory ? emailCatLabel(m.aiCategory) : '—';
  const acts = (s.suggestForId === m.id && s.suggestions.length) ? s.suggestions : [];
  const actsHtml = acts.map(function(x, i){ return `<div class="cd-dt-act"><div class="cd-dt-num">${i + 1}</div><p>${escapeHtml(x)}</p></div>`; }).join('');
  const suggesting = s.suggestLoading && s.suggestForId === m.id;
  const summaryBlock = m.aiSummary
    ? `<div class="cd-dt-sub"><div class="cd-dt-sub-head">${icon('auto_awesome', 16)}<span>${escapeHtml(t('dashboard.email.split.aiSummaryTitle'))}</span></div><p class="cd-dt-sum">${escapeHtml(m.aiSummary)}</p></div>`
    : '';
  const actionsInner = acts.length
    ? actsHtml
    : `<button class="cd-dt-suggest-btn" data-action="suggest-email" data-id="${escapeHtml(m.id)}">${icon('auto_awesome', 15)}<span>${escapeHtml(suggesting ? t('dashboard.email.split.analyzing') : t('dashboard.email.split.getSuggestion'))}</span></button>`;
  return `
    <div class="cd-dt-card ${prioCls}">
      <div class="cd-dt-accent"></div>
      <div class="cd-dt-body">
        <div class="cd-dt-head">
          <div class="cd-dt-sender">
            <div class="cd-dt-ava-ring"><div class="cd-dt-ava" style="background:${grad(dispName)};">${escapeHtml(initials(f.name, f.email))}</div></div>
            <div class="cd-dt-sender-txt">
              <div class="cd-dt-name">${escapeHtml(dispName)}</div>
              <div class="cd-dt-email">${escapeHtml(f.email)}</div>
            </div>
          </div>
          <div class="cd-dt-date">${escapeHtml(when)}</div>
        </div>
        <div class="cd-dt-subject">${escapeHtml(m.subject || t('dashboard.email.noSubject'))}</div>
        <div class="cd-dt-insights">
          <div class="cd-dt-ins">
            <div class="cd-dt-ins-lbl">${icon('sell', 14)}<span>${escapeHtml(t('dashboard.email.split.category'))}</span></div>
            <div class="cd-dt-ins-val">${escapeHtml(catLabel)}</div>
          </div>
          <div class="cd-dt-ins">
            <div class="cd-dt-ins-lbl">${icon('local_fire_department', 14)}<span>${escapeHtml(t('dashboard.email.split.priority'))}</span></div>
            <div class="cd-dt-ins-val ${prioCls}">${escapeHtml(prioLabel)}</div>
          </div>
          <div class="cd-dt-ins">
            <div class="cd-dt-ins-lbl">${icon('schedule', 14)}<span>${escapeHtml(t('dashboard.email.split.replyBy'))}</span></div>
            <div class="cd-dt-ins-val cd-dt-time">${escapeHtml(replyWindow)}</div>
          </div>
        </div>
        ${summaryBlock}
        <div class="cd-dt-sub">
          <div class="cd-dt-sub-head">${icon('checklist', 16)}<span>${escapeHtml(t('dashboard.email.split.suggestedActions'))}</span></div>
          ${actionsInner}
        </div>
        <div class="cd-dt-actions">
          <button class="cd-dt-btn cd-dt-btn-main">${icon('reply', 15)}<span>${escapeHtml(t('dashboard.email.split.quickReply'))}</span></button>
          <button class="cd-dt-btn">${icon('star', 15)}<span>${escapeHtml(t('dashboard.email.split.markFlag'))}</span></button>
          <button class="cd-dt-btn">${icon('archive', 15)}<span>${escapeHtml(t('dashboard.email.split.archive'))}</span></button>
        </div>
        <div class="cd-dt-mailbody">${escapeHtml(m.body || m.snippet || t('dashboard.email.noContent'))}</div>
      </div>
    </div>`;
}

export function splitView() {
  const list = s.emails.length ? s.emails.map(splitRow).join('') : `<div class="cd-empty" style="padding:20px;">${escapeHtml(t('dashboard.email.noEmails'))}</div>`;
  return `
    <div class="cd-split">
      <div class="cd-split-list">${list}</div>
      <div class="cd-split-detail">${splitDetail()}</div>
    </div>`;
}

export function emailFrom(raw) {
  const str = String(raw || '');
  const m = str.match(/^(.*?)<(.+?)>\s*$/);
  if (m) { const nm = m[1].trim().replace(/^"|"$/g, ''); return { name: nm || m[2].trim(), email: m[2].trim() }; }
  return { name: str, email: str };
}

export function aiCatBadge(cat) {
  if (!cat) return '';
  const label = emailCatLabel(cat);
  return `<span class="cd-cat cd-cat-${escapeHtml(cat)}">${escapeHtml(label)}</span>`;
}

export function emailRow(m) {
  const f = m.fromEmail ? { name: m.fromName || m.fromEmail, email: m.fromEmail } : emailFrom(m.from);
  let when = '';
  const rawDate = m.receivedAt || m.date;
  if (rawDate) { when = fmtDateTime(rawDate) || String(rawDate); }
  const analyzing = s.analyzingIds && s.analyzingIds.has(m.id);
  const right = m.aiCategory
    ? aiCatBadge(m.aiCategory)
    : `<button class="cd-btn-ghost" data-action="analyze-one" data-id="${escapeHtml(m.id)}">${escapeHtml(analyzing ? t('dashboard.email.analyzing') : t('dashboard.email.analyzeOne'))}</button>`;
  const aiBlock = (m.aiSummary || m.aiAction)
    ? `<div class="cd-mail-ai">
        ${m.aiSummary ? `<div class="cd-mail-ai-sum">${escapeHtml(m.aiSummary)}</div>` : ''}
        ${m.aiAction ? `<div class="cd-mail-ai-act">${icon('bolt', 13)}<span>${escapeHtml(m.aiAction)}</span></div>` : ''}
      </div>`
    : '';
  // Ungelesen: blauer Punkt + kräftigere Schrift — wie in jedem Mailprogramm.
  const unread = !m.readAt ? ' cd-mail-unread' : '';
  return `
    <div class="cd-mail-row${unread}">
      <span class="cd-mail-dot" aria-hidden="true"></span>
      <div class="cd-ava" style="background:${grad(f.name || f.email)};">${escapeHtml(initials(f.name, f.email))}</div>
      <div class="cd-mail-main" data-action="open-email" data-id="${escapeHtml(m.id)}" style="cursor:pointer;">
        <div class="cd-mail-top">
          <span class="cd-mail-from">${escapeHtml(f.name || f.email)}</span>
          <span class="cd-mail-date">${escapeHtml(when)}</span>
        </div>
        <div class="cd-mail-subject">${escapeHtml(m.subject || t('dashboard.email.noSubject'))}</div>
        <div class="cd-mail-snippet">${escapeHtml(m.snippet || '')}</div>
        ${aiBlock}
      </div>
      <div class="cd-mail-right">${right}<button class="cd-mail-del" data-action="delete-email" data-id="${escapeHtml(m.id)}" title="${escapeHtml(t('common.delete'))}">×</button></div>
    </div>`;
}

export function emailPage() {
  let body;
  if (s.emailLoading) body = `<div class="cd-muted">${escapeHtml(t('dashboard.loading'))}</div>`;
  else if (s.emailError) body = `<div class="cd-empty">${escapeHtml(s.emailError)}</div>`;
  else if (!s.emailAccount && !s.emails.length) body = `
    <div class="cd-connect">
      <div class="cd-connect-ic">${icon('mail', 30)}</div>
      <div class="cd-connect-title">${escapeHtml(t('dashboard.email.noAccountTitle'))}</div>
      <div class="cd-connect-sub">${escapeHtml(t('dashboard.email.noAccountSub'))}</div>
      <button class="cd-btn-primary cd-btn-sm" data-action="connect-gmail">${icon('add', 16)}${escapeHtml(s.connecting ? t('dashboard.email.connecting') : t('dashboard.email.connectGmail'))}</button>
    </div>`;
  else if (!s.emails.length) body = `<div class="cd-empty">${escapeHtml(t('dashboard.email.noEmailsFound'))}</div>`;
  else body = s.emails.map(emailRow).join('');
  return `
    <header class="cd-topbar">
      <div class="cd-greeting">
        <h1>${escapeHtml(t('dashboard.nav.email'))}</h1>
        <p>${s.emailAccount ? escapeHtml(s.emailAccount) : escapeHtml(t('dashboard.email.latestReceived'))}</p>
      </div>
      <button class="cd-btn-ghost cd-btn-temp" data-action="toggle-layout" title="${escapeHtml(t('dashboard.email.toggleLayoutTitle'))}">${icon('dashboard', 15)}${escapeHtml(s.emailLayout === 'split' ? t('dashboard.email.listView') : t('dashboard.email.splitView'))}</button>
      <button class="cd-btn-ghost" data-action="analyze-all">${icon('auto_awesome', 15)}${escapeHtml(s.analyzingAll ? t('dashboard.email.analyzing') : t('dashboard.email.analyzeAll'))}</button>
      <button class="cd-btn-ghost cd-btn-temp" data-action="reanalyze-all" title="${escapeHtml(t('dashboard.email.reanalyzeTitle'))}">${icon('refresh', 15)}${s.analyzingAll ? '...' : escapeHtml(t('dashboard.email.reanalyzeAll'))}</button>
      <button class="cd-btn-primary" data-action="connect-gmail">${icon('add', 16)}${escapeHtml(s.connecting ? t('dashboard.email.connecting') : t('dashboard.email.connectGmail'))}</button>
      <button class="cd-bell" data-action="reload-email" title="${escapeHtml(t('dashboard.refresh'))}">${icon('refresh', 18)}</button>
    </header>
    ${s.emailSyncWarning ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(s.emailSyncWarning)}</span></div>` : ''}
    ${can('email:read') ? emailKpis() : ''}
    ${s.emailLayout === 'split' ? '' : filterBar()}
    ${s.emailLayout === 'split'
      ? splitView()
      : `<div class="cd-card">
      <div class="cd-card-head">
        <div>
          <div class="cd-card-title">${escapeHtml(t('dashboard.inbox'))}</div>
          <div class="cd-card-sub">${escapeHtml(t('dashboard.email.latestReceived'))}</div>
        </div>
      </div>
      <div>${body}</div>
    </div>
    ${emailModal()}`}`;
}



